import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SignInCredentials } from 'src/app/core/models/SignInCredentials';
import { IAppState } from 'src/app/core/store/interfaces/IAppState';
import { Store } from '@ngrx/store';
import { CoreActions, signInAttempt } from 'src/app/core/store/actions';
import { Actions, ofType } from '@ngrx/effects';
import { AuthService } from 'src/app/core/services/auth';
import { NotificationService } from 'src/app/core/services/notification';
import { filter, map } from 'rxjs/operators';
import { DefaultErrorResponse } from 'src/app/core/models/DefaultErrorResponse';
import { selectTenantIdWithoutBranch } from 'src/app/core/store/selectors';
import { Subscription } from 'rxjs';
import { LoaderService } from 'src/app/core/services/loader';
import { ConfigService } from 'src/app/core/services/config';
import { isEmail, onlyNumbers } from 'src/app/core/utils/helpers';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  formGroup!: FormGroup;

  shouldShowInactivated = true;

  InactivatedEmailError$ = this.actions$.pipe(
    ofType(CoreActions.SignInError),
    filter(({ payload }: { payload: DefaultErrorResponse }) => payload?.message === 'Confirme o e-mail para acessar.'),
    map(Boolean)
  );

  email!: string;

  sentEmailConfirmation!: boolean;

  confirmationMessage!: string;

  tenantId!: string;

  tenantIdSub!: Subscription;

  emailNonConfirmedText = this.config.getConfiguration('EMAIL_NON_CONFIRMED_TEXT');

  showResendEmailLink = this.config.getConfiguration('SHOW_RESEND_EMAIL_LINK');

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<IAppState>,
    private actions$: Actions,
    private authService: AuthService,
    private notification: NotificationService,
    private loader: LoaderService,
    private config: ConfigService
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.setTenantId();
  }

  ngOnDestroy(): void {
    this.tenantIdSub.unsubscribe();
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const signInCredentials = this.getSignInCredentials();

    this.store.dispatch(signInAttempt({ payload: signInCredentials }));
  }

  resendEmailConfirmation(): void {
    const emailToResend = this.formGroup.get('cgc')?.value;

    this.loader.show('Reenviando e-mail...');
    this.authService
      .resendEmailConfirmation(emailToResend)
      .subscribe((email) => {
        this.shouldShowInactivated = false;
        this.sentEmailConfirmation = true;
        this.confirmationMessage = 'O e-mail de confirmação foi enviado para o e-mail com sucesso!';
        this.notification.success('E-mail reenviado para ' + email);
      })
      .add(() => this.loader.hide());
  }

  private setTenantId(): void {
    this.tenantIdSub = this.store.select(selectTenantIdWithoutBranch).subscribe((tenantId) => {
      if (!tenantId) return;
      this.tenantId = tenantId;
    });
  }

  private buildForm(): void {
    this.formGroup = this.formBuilder.group({
      cgc: ['', [Validators.required, this.cgcOrEmailValidator]],
      password: ['', [Validators.required]],
    });
  }

  onCgcInput(): void {
    const control = this.formGroup.get('cgc');
    const input = String(control?.value || '');
    const trimmedInput = input.trim();
    const hasLetters = /[a-zA-Z]/.test(trimmedInput);
    const shouldUseEmailMode = trimmedInput.includes('@') || hasLetters;

    if (shouldUseEmailMode) {
      control?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const digits = onlyNumbers(trimmedInput).slice(0, 14);
    const formatted = this.formatCpfCnpj(digits);

    if (formatted !== input) {
      control?.setValue(formatted, { emitEvent: false });
    }

    control?.updateValueAndValidity({ emitEvent: false });
  }

  private cgcOrEmailValidator = (control: { value: string }) => {
    const input = String(control?.value || '').trim();

    if (!input) {
      return null;
    }

    const hasLetters = /[a-zA-Z]/.test(input);
    if (input.includes('@') || hasLetters) {
      return isEmail(input) ? null : { invalidCgcOrEmail: true };
    }

    const digits = onlyNumbers(input);
    const isValidDocument = digits.length === 11 || digits.length === 14;

    return isValidDocument ? null : { invalidCgcOrEmail: true };
  };

  private getSignInCredentials(): SignInCredentials {
    const value = this.formGroup.getRawValue();
    const cgcValue = String(value.cgc || '').trim();
    const shouldSendAsEmail = cgcValue.includes('@') || /[a-zA-Z]/.test(cgcValue);

    return new SignInCredentials({
      cgc: shouldSendAsEmail ? cgcValue : onlyNumbers(cgcValue),
      password: value.password,
      tenantId: this.tenantId,
    });
  }

  private formatCpfCnpj(digits: string): string {
    if (digits.length <= 11) {
      const p1 = digits.slice(0, 3);
      const p2 = digits.slice(3, 6);
      const p3 = digits.slice(6, 9);
      const p4 = digits.slice(9, 11);
      return [p1, p2, p3].filter(Boolean).join('.') + (p4 ? `-${p4}` : '');
    }

    const p1 = digits.slice(0, 2);
    const p2 = digits.slice(2, 5);
    const p3 = digits.slice(5, 8);
    const p4 = digits.slice(8, 12);
    const p5 = digits.slice(12, 14);
    return `${p1}.${p2}.${p3}/${p4}${p5 ? `-${p5}` : ''}`;
  }
}
